"use client"

import { Button } from "@/components/ui/button"
import {
    Form,
} from "@/components/ui/form"

import { zodResolver } from "@hookform/resolvers/zod"
import {FormProvider, useForm} from "react-hook-form"
import { z } from "zod"
import {PersonalInfoForm} from "@/components/booking/form/PersonalInfoForm.tsx";
import {BookingFormSchema} from "@/components/booking/form/BookingFormSchema.ts";
import {DatesForm} from "@/components/booking/form/DatesForm.tsx";
import {BinForm} from "@/components/booking/form/BinForm.tsx";
import {AddressForm} from "@/components/booking/form/AddressForm.tsx";
import {PaymentsForm, PaymentsFormRef} from "@/components/booking/form/PaymentsForm.tsx";
import {TermsForm} from "@/components/booking/form/TermsForm.tsx";
import {PricingCalculator, PricingCalculatorRef} from "@/components/booking/PricingCalculator.tsx";
import {BookingDuration} from "@/lib/enum/BookingDuration.ts";
import {useContext, useEffect, useRef, useState} from "react";
import {OscarContext, OscarContextType} from "@/components/context/OscarContext.tsx";
import {OscarFormStateRequest} from "@/lib/oscar/ApiModels.ts";
import Api from "@/lib/oscar/Api.ts";
import {ExclamationCircleIcon} from "@heroicons/react/24/solid";
import {DateTime} from "@easepick/datetime";
import {BinKindForm} from "@/components/booking/form/BinKindForm.tsx";

export function BookingForm() {
    const { authToken, setLoading, setBooking, appConfig, localConfig } = useContext(OscarContext) as OscarContextType
    const paymentRef = useRef<PaymentsFormRef>(null);
    const calcRef = useRef<PricingCalculatorRef>(null);
    const [submitError, setSubmitError] = useState<string|null>(null);
    const [lastFormState, setLastFormState] = useState<string|null>(null);
    const submitErrorRef = useRef<HTMLDivElement>(null);

    const form = useForm<z.infer<typeof BookingFormSchema>>({
        resolver: zodResolver(BookingFormSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            duration: BookingDuration.WEEK,
            startDate: undefined,
            endDate: undefined,
            binKind: localConfig.defaultBinKind,
            binSize: undefined,
            binType: undefined,
            extras: {},
            address: undefined,
            suburb: undefined,
            postcode: undefined,
            state: undefined,
            lat: undefined,
            lng: undefined,
            distanceCharge: undefined,
            notes: "",
            terms: false,
            allowPromo: false,

            commDepartment: "",
            commCaseName: "",
            commCaseEmail: "",
            commInvoiceEmail: "",
            commCasePhone: "",
            commPurchaseOrder: "",
        },
    });

    /**
     * Handle form submission.
     * @param values
     */
    async function onSubmit(values: z.infer<typeof BookingFormSchema>) {
        setSubmitError(null);
        setLoading(true);

        // Process payment.
        const paymentData = await paymentRef.current?.getCardDetails();
        if (!paymentData) {
            setSubmitError("Something went wrong while checking your card, please try again later or call to place your booking.");
            setLoading(false);
            submitErrorRef.current?.scrollIntoView({ behavior: "smooth" });
            return;
        } else if (paymentData.status !== "OK" || !paymentData.token) {
            setSubmitError("Please check your card details and try again.");
            setLoading(false);
            submitErrorRef.current?.scrollIntoView({ behavior: "smooth" });
            return;
        }

        // Prepare data.
        const data = {
            community: false,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.phone,
            businessName: values.businessName,
            poNumber: values.poNumber,
            duration: values.duration,
            startDate: (new DateTime(values.startDate)).format("YYYY-MM-DD"),
            endDate: (new DateTime(values.endDate)).format("YYYY-MM-DD"),
            binKind: values.binKind,
            binSize: values.binSize,
            binType: values.binType,
            extras: values.extras,
            address: values.address,
            suburb: values.suburb,
            postcode: values.postcode,
            state: values.state,
            lat: values.lat,
            lng: values.lng,
            distanceCharge: values.distanceCharge,
            totalCharge: await calcRef.current?.getTotal(),
            notes: values.notes,
            paymentToken: paymentData.token,
            terms: values.terms,
            commDepartment: null,
            commCaseName: null,
            commCaseEmail: null,
            commCasePhone: null,
            commInvoiceEmail: null,
            commPurchaseOrder: null,
            allowPromo: values.allowPromo || null,
        } as OscarFormStateRequest;

        try {
            // Send data to the Oscar API
            const booking = await Api.submitBooking(authToken, data);
            setBooking(booking);
        } catch (e) {
            setSubmitError((e as Error).message);
            setLoading(false);
            submitErrorRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }

    useEffect(() => {
        /**
         * Update the form state.
         *
         * This function is called when the component mounts.
         */
        const updateFormState = async function(values: z.infer<typeof BookingFormSchema>) {
            // Prepare data.
            const extras: { [key: string]: number } = {};
            for (const key in values.extras) {
                if (values.extras[key]) {
                    extras[key] = values.extras[key];
                }
            }
            const data = {
                community: false,
                firstName: values.firstName || null,
                lastName: values.lastName || null,
                email: values.email || null,
                phone: values.phone || null,
                businessName: values.businessName || null,
                poNumber: values.poNumber || null,
                duration: values.duration || null,
                startDate: (values.startDate && (new DateTime(values.startDate)).format("YYYY-MM-DD")) || null,
                endDate: (values.endDate && (new DateTime(values.endDate)).format("YYYY-MM-DD")) || null,
                binKind: values.binKind || null,
                binSize: values.binSize || null,
                binType: values.binType || null,
                extras: extras,
                address: values.address || null,
                suburb: values.suburb || null,
                postcode: values.postcode || null,
                state: values.state || null,
                lat: values.lat || null,
                lng: values.lng || null,
                distanceCharge: values.distanceCharge || null,
                totalCharge: await calcRef.current?.getTotal(),
                notes: values.notes || null,
                paymentToken: null,
                terms: values.terms || null,
                commDepartment: null,
                commCaseName: null,
                commCaseEmail: null,
                commCasePhone: null,
                commInvoiceEmail: null,
                commPurchaseOrder: null,
                allowPromo: values.allowPromo || null,
            } as OscarFormStateRequest;

            await Api.sendState(authToken, data);
        }

        // Use setTimeout to update the message after 2000 milliseconds (2 seconds)
        const timeoutId = setInterval(() => {
            const values = form.getValues();
            const state = JSON.stringify(values);
            if (state == lastFormState) return;
            setLastFormState(state);
            updateFormState(values).then(() => { console.log("State updated!"); }).catch(err => { console.error("State error", err); });
        }, 5000);

        // Cleanup function to clear the timeout if the component unmounts
        return () => clearInterval(timeoutId);
    }, [authToken, form, lastFormState, setLastFormState]);

    return (
        <FormProvider {...form}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="os-w-full os-flex os-flex-col os-gap-4">
                    <PersonalInfoForm />
                    {(appConfig?.bookingData?.bagSizes?.length ?? 0) > 0 && (
                        <BinKindForm />
                    )}
                    <DatesForm />
                    <BinForm />
                    <AddressForm />
                    <PricingCalculator ref={calcRef} />
                    <div className={"os-app-row os-flex os-flex-col os-gap-4"}>
                        <PaymentsForm ref={paymentRef} />
                        <TermsForm />
                        {submitError && <div ref={submitErrorRef} className={"os-text-red-500 os-flex os-flex-row os-items-center os-gap-1 os-mb-4"}>
                            <ExclamationCircleIcon className={"os-h-5 os-w-5"} />
                            <div>{submitError}</div>
                        </div>}
                        <Button type="submit" className={"os-brand-btn"}>Place Booking</Button>
                    </div>
                </form>
            </Form>
        </FormProvider>
    )
}